"use client"

import { useEffect, useId, useState, type Dispatch, type SetStateAction } from "react"
import { FormikProvider, useFormik } from "formik"
import { useTranslations } from "next-intl"
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react"
import { usePermissions } from "@/hooks/use-permissions"
import { AI_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useAgentCreationKnowledgeQuery } from "@/hooks/queries/agent-creation"
import { useKnowledgeBaseUrlImportJobsQuery } from "@/hooks/queries/knowledge-base-url-import-jobs"
import { useKnowledgeBaseSocialImportJobsQuery } from "@/hooks/queries/knowledge-base-social-import-jobs"
import { Button } from "@/components/ui/button"
import { KnowledgeSelectionTable } from "./knowledge-selection-table"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import type { CreationAgentType } from "@/lib/agents/creation/types"
import { KnowledgeImportOptions, type KnowledgeImportJob, type KnowledgeImportSource } from "./knowledge-import-options"
import { WizardStepActions } from "./wizard-step-actions"

type KnowledgeChoice = "" | "existing" | "import"

export function KnowledgeStep({ type, selected, onChange, onNext, onSkip, onBack }: {
  type: CreationAgentType
  selected: string[]
  onChange: Dispatch<SetStateAction<string[]>>
  onNext: () => void
  onSkip: () => void
  onBack: () => void
}) {
  const t = useTranslations("AgentCreation")
  const choiceText = useTranslations("AgentCreationKnowledgeChoice")
  const notices = useTranslations("AgentCreationNotices")
  const choiceId = useId()
  const { can } = usePermissions()
  const canManage = can(AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE)
  const knowledge = useAgentCreationKnowledgeQuery(type, canManage)
  const { refetch: refreshKnowledge } = knowledge
  const [job, setJob] = useState<KnowledgeImportJob | null>(null)
  const [importSource, setImportSource] = useState<KnowledgeImportSource>("website")
  const formik = useFormik<{ choice: KnowledgeChoice }>({
    initialValues: { choice: "" },
    onSubmit: async () => {},
  })
  const setChoice = formik.setFieldValue
  useEffect(() => {
    // Keep the import surface mounted while new documents populate a previously empty library.
    if (knowledge.data?.length === 0 && formik.values.choice === "") void setChoice("choice", "import")
  }, [knowledge.data?.length, formik.values.choice, setChoice])
  const hasExisting = Boolean(knowledge.data?.length)
  const choice = hasExisting ? formik.values.choice : "import"
  const urlJobs = useKnowledgeBaseUrlImportJobsQuery({ enabled: job?.source === "website" })
  const socialJobs = useKnowledgeBaseSocialImportJobsQuery({ enabled: Boolean(job && job.source !== "website") })
  const selectedJob = [...(urlJobs.data?.jobs ?? []), ...(socialJobs.data?.jobs ?? [])].find(item => item.id === job?.id)
  const importedFolderId = selectedJob?.result?.folderId
  const importedDocumentId = selectedJob?.result?.documentId

  useEffect(() => {
    if (selectedJob?.status !== "completed" && selectedJob?.status !== "failed") return
    let cancelled = false
    void refreshKnowledge().then(({ data }) => {
      if (cancelled || selectedJob.status !== "completed" || !data) return
      const importedIds = data.filter(document =>
        (typeof importedFolderId === "string" && document.folderId === importedFolderId) ||
        (typeof importedDocumentId === "string" && document.id === importedDocumentId),
      ).map(document => document.id)
      if (importedIds.length) onChange(current => [...new Set([...current, ...importedIds])])
    })
    return () => { cancelled = true }
  }, [selectedJob?.id, selectedJob?.status, importedFolderId, importedDocumentId, refreshKnowledge, onChange])

  async function documentCreated(id: string) {
    onChange(current => [...new Set([...current, id])])
    await refreshKnowledge()
  }

  async function acceptJob(nextJob: KnowledgeImportJob) {
    setJob(nextJob)
    setImportSource(nextJob.source)
    await formik.setFieldValue("choice", "existing")
    if (nextJob.source === "website") await urlJobs.refetch()
    else await socialJobs.refetch()
  }

  let content
  if (!canManage) {
    content = <p><span>{t("knowledgePermission")}</span></p>
  } else if (knowledge.isLoading) {
    content = <p role="status"><span>{t("preparing")}</span></p>
  } else if (knowledge.error && !knowledge.data) {
    content = null
  } else {
    content = (
      <>
        {hasExisting ? (
          <fieldset className="space-y-4">
            {formik.values.choice === "" ? <legend className="text-base font-medium"><span>{choiceText("question")}</span></legend> : null}
            <RadioGroup
              aria-label={choiceText("question")}
              value={formik.values.choice}
              onValueChange={value => {
                if (value !== "existing" && value !== "import") return
                void formik.setFieldValue("choice", value)
                if (value === "import") onChange([])
              }}
              className="flex flex-wrap gap-x-6 gap-y-3"
            >
              <label htmlFor={`${choiceId}-existing`} className="flex cursor-pointer items-center gap-2 text-sm">
                <RadioGroupItem id={`${choiceId}-existing`} value="existing" />
                <span>{choiceText("useExisting")}</span>
              </label>
              <label htmlFor={`${choiceId}-import`} className="flex cursor-pointer items-center gap-2 text-sm">
                <RadioGroupItem id={`${choiceId}-import`} value="import" />
                <span>{choiceText("importNew")}</span>
              </label>
            </RadioGroup>
          </fieldset>
        ) : null}
        {choice === "existing" ? (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground"><span>{notices("coverage")}</span></p>
            <KnowledgeSelectionTable documents={knowledge.data ?? []} selected={selected} onChange={onChange} />
          </div>
        ) : null}
        {choice === "import" ? (
          <KnowledgeImportOptions
            initialSource={importSource}
            onDocumentCreated={documentCreated}
            onDocumentsImported={async ids => {
              onChange(current => [...new Set([...current, ...ids])])
              await refreshKnowledge()
            }}
            onImported={() => void formik.setFieldValue("choice", "existing")}
            onJobAccepted={acceptJob}
            onViewProgress={() => void formik.setFieldValue("choice", "existing")}
            onSkip={onSkip}
          />
        ) : null}
      </>
    )
  }

  return (
    <FormikProvider value={formik}>
      <div className="space-y-6">
        {knowledge.error ? <div role="alert"><p>{knowledge.error.message}</p><Button onClick={() => void refreshKnowledge()}><span>{t("retry")}</span></Button></div> : null}
        {job ? (
          <div role="status" className="flex items-center gap-3 rounded-lg border p-3">
            <ImportStatusIcon status={selectedJob?.status} />
            <span>{importStatusLabel(selectedJob?.status, t)}</span>
            {selectedJob?.status === "failed" ? <Button variant="outline" onClick={() => { setImportSource(job.source); setJob(null); void formik.setFieldValue("choice", "import") }}><span>{t("retry")}</span></Button> : null}
          </div>
        ) : null}
        {content}
        <WizardStepActions>
          <Button variant="outline" onClick={onBack}><span>{t("back")}</span></Button>
          <Button variant="ghost" onClick={onSkip}><span>{t("skip")}</span></Button>
          {choice === "existing" ? <Button disabled={selected.length === 0 || knowledge.isFetching} onClick={onNext}><span>{t("next")}</span></Button> : null}
        </WizardStepActions>
      </div>
    </FormikProvider>
  )
}

function ImportStatusIcon({ status }: { status: string | undefined }) {
  switch (status) {
    case "completed": return <CheckCircle2 aria-hidden="true" className="h-5 w-5 shrink-0 text-green-600" />
    case "failed": return <AlertCircle aria-hidden="true" className="h-5 w-5 shrink-0 text-destructive" />
    default: return <Loader2 aria-hidden="true" className="h-5 w-5 shrink-0 animate-spin text-blue-600" />
  }
}

function importStatusLabel(status: string | undefined, t: ReturnType<typeof useTranslations<"AgentCreation">>) {
  switch (status) {
    case "completed": return t("importComplete")
    case "failed": return t("importFailed")
    default: return t("importing")
  }
}
